import { ButtonGradientPrimary, FOCUS_RING, Icon } from '@amelio/ui-library';
import { useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import HelpInfoIcon from '../HelpInfoIcon';
import KeyResultFields from '../KeyResultFields';
import { MeasurementSpine, MeasurementSpineHead, MeasurementSpineItem } from '../MeasurementSpine';
import SectionHeading from '../SectionHeading';

import type { EmployeeOption } from '../../hooks/useKeyResultResponsibles';
import type { ObjectiveFormData } from '@/pages/Objectives/validation';
import type { Control, UseFormSetValue } from 'react-hook-form';

import { GLASS_SURFACE } from '@/components/objectives/objectiveStyles';
import { DEFAULT_KEY_RESULT } from '@/pages/Objectives/validation';
import { validationMessageKey } from '@/utils/validationMessage';

interface ObjectiveMeasurementsSectionProps {
    control: Control<ObjectiveFormData>;
    setValue: UseFormSetValue<ObjectiveFormData>;
    employeeOptions: EmployeeOption[];
    isTeam: boolean;
    /** Scrolls to and focuses the title field -- the "Inscrire le titre de l'objectif" button
     * below has nowhere else to send the user. */
    onRequestTitle: () => void;
}

/**
 * "Éléments de mesure": one glass frame holding the section header, the live echo of the
 * objective title and the key-result cards hung off the dashed purple spine.
 */
export default function ObjectiveMeasurementsSection({
    control,
    setValue,
    employeeOptions,
    isTeam,
    onRequestTitle,
}: ObjectiveMeasurementsSectionProps) {
    const { t } = useTranslation();
    const { fields, append, remove } = useFieldArray({ control, name: 'keyResults' });
    const { errors } = useFormState({ control });
    // Live echo of the title into the "Objectif principal" inset: reads straight from the form
    // so it updates on every keystroke, same as the prototype.
    const title = useWatch({ control, name: 'title' });

    // `@hookform/resolvers` files an array-level error under `.root` only while at least one row
    // is mounted; with an empty array — the only case that can trigger `keyResultRequired` — it
    // lands on `keyResults` itself, so both spellings have to be read.
    const keyResultsError = errors.keyResults?.root?.message ?? errors.keyResults?.message;

    return (
        <div
            className={`obj-section ${GLASS_SURFACE}`}
            data-testid={'objective-form-key-results'}
        >
            <div className={'flex flex-col gap-[48px]'}>
                <div className={'flex flex-col gap-2'}>
                    <SectionHeading
                        icon={'essential-sort'}
                        title={t('objectives.form.measurementElements')}
                    />

                    <div className={'flex items-center gap-3 pl-9'}>
                        <HelpInfoIcon />

                        <p className={'text-obj-muted text-[14px] font-medium'}>
                            {t('objectives.form.measurementElementsHelp')}
                        </p>
                    </div>
                </div>

                {keyResultsError && (
                    <p
                        role={'alert'}
                        className={'text-error text-[14px]'}
                    >
                        {t(validationMessageKey(keyResultsError))}
                    </p>
                )}

                <MeasurementSpineHead
                    surfaceClassName={
                        'bg-gradient-to-r from-[rgba(138,56,245,0.08)] to-[rgba(169,143,203,0.08)]'
                    }
                    dataTestId={'objective-form-main-objective'}
                >
                    <div className={'flex flex-col gap-1.5'}>
                        <span className={'text-obj-gray text-[14px] font-medium'}>
                            {t('objectives.form.mainObjective')}
                        </span>

                        {title?.trim() ? (
                            <span className={'text-navy text-[20px] font-medium'}>{title}</span>
                        ) : (
                            <button
                                type={'button'}
                                onClick={onRequestTitle}
                                className={`${FOCUS_RING} text-obj-gray/60 text-left text-[20px] font-medium`}
                                data-testid={'objective-form-enter-title'}
                            >
                                {t('objectives.form.enterTitle')}
                            </button>
                        )}
                    </div>
                </MeasurementSpineHead>

                <MeasurementSpine>
                    {fields.map((field, index) => (
                        <MeasurementSpineItem
                            key={field.id}
                            markerTop={24}
                            isLast={index === fields.length - 1}
                        >
                            <KeyResultFields
                                control={control}
                                setValue={setValue}
                                index={index}
                                onRemove={() => remove(index)}
                                canRemove={fields.length > 1}
                                employeeOptions={employeeOptions}
                                isTeam={isTeam}
                            />
                        </MeasurementSpineItem>
                    ))}

                    <div>
                        <ButtonGradientPrimary
                            onClick={() => append({ ...DEFAULT_KEY_RESULT })}
                            size={'sm'}
                            leftIcon={
                                <Icon
                                    name={'essential-add'}
                                    size={'sm'}
                                    color={'inherit'}
                                />
                            }
                            dataTestId={'objective-form-add-key-result'}
                        >
                            {t('objectives.form.addMeasurementElement')}
                        </ButtonGradientPrimary>
                    </div>
                </MeasurementSpine>
            </div>
        </div>
    );
}
